import { useEffect, useRef, useState } from 'react';
import type { DevFlags, SystemStatus } from '@torapon/shared';
import { DevPanel, DevUnlockDialog } from '../components/DevMode';
import { OfficeContactPanel } from '../components/OfficeContact';
import { SettingsPanel } from '../components/SettingsPanel';
import { api, HttpError } from '../lib/api';
import { fmtDateTime } from '../lib/format';
import { useRealtimeConnected } from '../lib/realtime';

/** システムの状態（仕様書「システムの状態画面」。MVP は最小限） */
export function SystemPage() {
  const [s, setS] = useState<SystemStatus | null>(null);
  const [err, setErr] = useState('');
  const live = useRealtimeConnected();
  // 「サーバーの版」を 3 秒以内に続けて 10 回押すと、開発者モードの合言葉を聞く
  const taps = useRef({ n: 0, at: 0 });
  const [asking, setAsking] = useState(false);
  const [dev, setDev] = useState<{ password: string; flags: DevFlags } | null>(null);
  const tap = () => {
    const now = Date.now();
    taps.current = { n: now - taps.current.at < 3000 ? taps.current.n + 1 : 1, at: now };
    if (taps.current.n >= 10) { taps.current.n = 0; setAsking(true); }
  };
  useEffect(() => { api.get<SystemStatus>('/v1/office/system/status').then(setS).catch((e) => setErr(e instanceof HttpError ? e.message : '読み込めませんでした')); }, []);
  if (err) return <div className="page"><p className="err" role="alert">{err}</p></div>;
  if (!s) return <div className="page"><p className="muted">読み込み中…</p></div>;
  const cards: [string, string, string?][] = [
    ['最後のバックアップ', s.lastBackupAt ? fmtDateTime(s.lastBackupAt) : 'まだありません', s.lastBackupAt ? undefined : '毎朝 3:00 の自動バックアップは本番の準備のときにつなぎます'],
    ['今日の操作', `${s.writesToday} 件`, '操作ログに残った件数'],
    ['勤務中', `${s.openShifts} 人`],
    ['位置が届いていない端末', `${s.silentDevices} 台`, '勤務中で 10 分以上届いていない'],
    ['即時の更新', live ? 'つながっています' : 'つなぎ直しています'],
    ['サーバーの版', s.apiVersion],
  ];
  return (
    <div className="page" style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        {cards.map(([label, value, note]) => (
          <section key={label} className="panel" style={{ padding: 16 }} aria-label={label} onClick={label === 'サーバーの版' ? tap : undefined}>
            <small className="muted">{label}</small>
            <b style={{ display: 'block', fontSize: 22, color: label === '位置が届いていない端末' && s.silentDevices ? 'var(--alert)' : undefined }}>{value}</b>
            {note && <small className="muted">{note}</small>}
          </section>
        ))}
      </div>
      <SettingsPanel />
      <OfficeContactPanel />
      {asking && <DevUnlockDialog onClose={() => setAsking(false)} onOpen={(password, flags) => { setAsking(false); setDev({ password, flags }); }} />}
      {dev && <DevPanel password={dev.password} initial={dev.flags} />}
      <section className="panel">
        <div style={{ padding: '14px 16px 0' }}><b>アプリの版ごとの台数</b></div>
        <table><thead><tr><th>版</th><th>台数</th></tr></thead>
          <tbody>{s.appVersions.length === 0 ? <tr><td colSpan={2} className="muted">まだ端末がつながっていません</td></tr> : s.appVersions.map((v) => <tr key={v.version}><td className="num">{v.version}</td><td className="num">{v.count} 台</td></tr>)}</tbody></table>
      </section>
    </div>
  );
}
